<script setup lang="ts">
import type { ConfigElement } from '~/types/config';
import { useConfigParserStore } from '~/stores/configParser';
import { computed } from 'vue';
import { formatTextWithLinks } from '~/Common/StringOperations';


const configParserStore = useConfigParserStore();

const props = defineProps<{
  element: ConfigElement;
  getNextInputId: (element: ConfigElement, categoryGroup: any) => string | null;
  categoryGroup: any;
  configValues: Record<string, string>;
  isEnabled?: boolean;
  readOnly?: boolean;
}>();

const emit = defineEmits<{
  'focus:next': [nextId: string];
}>();

// Compute the actual element value from props.configValues
const elementValue = computed(() => {
  return props.configValues[props.element.elementPath] ?? props.element.elementValue ?? '';
});

/**
 * Updates the value of the element
 * @param value - The new value of the element
 */
const updateElementValue = (value: string) => {
  configParserStore.updateElementValue(props.element.elementPath, value);
};

/**
 * Updates the enable flag for an element
 * @param elementId - The ID of the element
 * @param enabled - Whether the element is enabled
 */
const updateEnableFlag = (elementId: string, enabled: boolean) => {
  if (elementId) {
    const enableValue = enabled ? '1' : '';
    configParserStore.updateElementValue(`enable_${elementId}`, enableValue);
  }
};


/**
 * Handles the focus on the next input field.
 */
const handleFocusNext = () => {
  const nextId = props.getNextInputId(props.element, props.categoryGroup) || 'save';
  emit('focus:next', nextId);
};


</script>

<template>
  <div class="w-full flex flex-row align-items-center mb-2" :key="`field-${props.element.id}`">
    <div class="w-5 text-left">
      <label :for="`input-${props.element.id}`" v-html="formatTextWithLinks(props.element.translation)">
      </label>
      <span v-if="element.isRequired" class="text-red-500">
        *
      </span>
    </div>
    <div class="w-5 flex justify-content-start">
      <InputSwitch
        v-if="props.element.htmlType === 'checkbox'"
        :inputId="`input-${props.element.id}`"
        :modelValue="elementValue === '1'"
        @update:modelValue="(val: any) => { updateElementValue(val ? '1' : ''); }"
        binary
        :disabled="!props.isEnabled || props.readOnly"
        :key="`inputSwitch-${props.element.id}`"
      />

      <Dropdown
        v-else-if="props.element.htmlType === 'select'"
        :inputId="`input-${props.element.id}`"
        :model-value="elementValue"
        :options="props.element.options || []"
        option-label="label"
        option-value="value"
        @update:modelValue="(val: any) => { updateElementValue(String(val)); if (props.isEnabled) { updateEnableFlag(String(props.element.id), true); } }"
        class="w-full"
        :disabled="!props.isEnabled || props.readOnly"
        :key="`dropdown-${props.element.id}`"
      />

      <Password
        v-else-if="props.element.htmlType === 'password'"
        :inputId="`input-${props.element.id}`"
        v-model="elementValue"
        @input="(e: any) => updateElementValue(e.target.value)"
        class="w-full password-field"
        panel-class="w-full"
        input-class="w-full"
        :feedback="false"
        toggle-mask
        @change="(e: any) => { updateElementValue(elementValue); if (props.isEnabled) { updateEnableFlag(String(props.element.id), true); } }"
        :inputProps="{ autocomplete: 'new-password' }"
        @keydown.enter.prevent="handleFocusNext()"
        :disabled="!props.isEnabled || props.readOnly"
        :key="`password-${props.element.id}`"
      />

      <Textarea
        v-else-if="props.element.htmlType === 'textarea'"
        :inputId="`input-${props.element.id}`"
        v-model="elementValue"
        @input="(e: any) => updateElementValue(e.target.value)"
        class="w-full"
        rows="3"
        @change="(e: any) => { updateElementValue(elementValue); if (props.isEnabled) { updateEnableFlag(String(props.element.id), true); } }"
        @keydown.enter.prevent="handleFocusNext()"
        :disabled="!props.isEnabled || props.readOnly"
        :key="`textarea-${props.element.id}`"
      />

      <InputText
        v-else-if="props.element.htmlType === 'text'"
        :id="`input-${props.element.id}`"
        v-model="elementValue"
        @input="(e: any) => updateElementValue(e.target.value)"
        class="w-full"
        @change="(e: any) => { updateElementValue(elementValue); if (props.isEnabled) { updateEnableFlag(String(props.element.id), true); } }"
        @keydown.enter.prevent="handleFocusNext()"
        :disabled="!props.isEnabled || props.readOnly"
        :key="`input-${props.element.id}`"
      />

      <InputText
        v-else-if="props.element.htmlType === 'read_only'"
        :id="`input-${props.element.id}`"
        :modelValue="elementValue"
        class="w-full border border-gray-300 rounded px-6 py-2 text-base box-border"
        readonly
        :key="`readonly-${props.element.id}`"
      />
    </div>
  </div>
</template>

<style scoped>
/* Base layout - prevent overlap on all screen sizes */
.w-full.flex.flex-row {
  gap: 1rem;
}

.w-5.text-left {
  flex: 0 0 35%;
  max-width: 35%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.w-5.flex.justify-content-start {
  flex: 1 1 60%;
  max-width: 60%;
  min-width: 0; /* Allow flex items to shrink below content size */
  position: relative; /* Positioning context for absolute children */
}

.p-inputtext,
.p-password-input {
  height: 2rem;
  min-height: 2rem;
  padding: 2px 8px;
}

/* Password field container - prevent overflow */
.password-field {
  width: 100% !important;
  max-width: 100% !important;
  position: relative;
  overflow: hidden;
}

.p-password {
  height: 2rem;
  width: 100% !important;
  max-width: 100% !important;
  position: relative;
  overflow: hidden;
}

/* Prevent password toggle icon from overflowing */
:deep(.p-password) {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  position: relative;
  overflow: hidden;
}

:deep(.p-password .p-input-icon-right) {
  width: 100% !important;
  max-width: 100% !important;
  position: relative;
  overflow: hidden;
}

:deep(.p-password .p-inputtext) {
  padding-right: 2.5rem !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

:deep(.p-password-toggle-mask) {
  position: absolute !important;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

:deep(.p-password .p-password-toggle-icon) {
  right: 0.5rem;
  position: absolute;
}

:deep(.p-password-input) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

/* Dropdown - force full text display */
:deep(.p-dropdown) {
  min-height: 2rem !important;
  height: auto !important;
  box-sizing: border-box !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
}

:deep(.p-dropdown .p-dropdown-trigger) {
  flex-shrink: 0 !important;
  width: 2.5rem !important;
}

:deep(.p-dropdown .p-dropdown-label) {
  line-height: 1.5 !important;
  min-height: 2rem !important;
  height: auto !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  padding: 0.25rem 0.5rem !important;
  padding-right: 2.5rem !important;
  flex: 1 1 auto !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  overflow: visible !important;
  text-overflow: clip !important;
  box-sizing: border-box !important;
}

:deep(.p-dropdown .p-inputtext) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
}

:deep(.p-dropdown-trigger) {
  width: 2.5rem !important;
  flex-shrink: 0 !important;
}

/* Dropdown panel styling */
:deep(.p-dropdown-panel) {
  font-size: 0.875rem;
}

:deep(.p-dropdown-items) {
  font-size: 0.875rem;
}

:deep(.p-dropdown-item) {
  font-size: 0.875rem;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  padding: 0.5rem 0.75rem;
  line-height: 1.4;
}

/* Responsive - stack label and input vertically when screen is small or dialog is constrained */
@media (max-width: 1280px) {
  .w-full.flex.flex-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .w-5 {
    width: 100% !important;
    max-width: 100% !important;
  }

  .w-5.text-left {
    margin-bottom: 0.5rem;
  }

  .w-5.flex {
    width: 100% !important;
  }

  /* Reduce dropdown font size for better text visibility */
  .p-dropdown {
    font-size: 0.875rem;
    height: auto;
    min-height: 2rem;
  }

  .p-dropdown-label {
    font-size: 0.875rem;
    white-space: normal;
    line-height: 1.3;
    padding: 0.25rem 0;
  }

  :deep(.p-dropdown-panel) {
    font-size: 0.875rem;
  }

  :deep(.p-dropdown-items) {
    font-size: 0.875rem;
  }

  :deep(.p-dropdown-item) {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.4;
  }

  :deep(.p-dropdown-item-label) {
    font-size: 0.875rem;
  }

  /* Adjust input field font sizes */
  .p-inputtext {
    font-size: 0.875rem;
  }

  .p-password-input {
    font-size: 0.875rem;
  }

  /* Ensure password toggle stays within bounds */
  :deep(.p-password) {
    max-width: 100%;
    overflow: hidden;
  }

  :deep(.p-password .p-inputtext) {
    padding-right: 2.5rem;
    font-size: 0.875rem;
  }

  /* Adjust label font size */
  label {
    font-size: 0.875rem;
  }
}

/* Extra small screens - further reduce sizes */
@media (max-width: 768px) {
  .p-dropdown {
    font-size: 0.8125rem;
    height: auto;
    min-height: 2rem;
  }

  .p-dropdown-label {
    font-size: 0.8125rem;
    white-space: normal;
    line-height: 1.3;
    padding: 0.25rem 0;
  }

  :deep(.p-dropdown-panel) {
    font-size: 0.8125rem;
  }

  :deep(.p-dropdown-items) {
    font-size: 0.8125rem;
  }

  :deep(.p-dropdown-item) {
    font-size: 0.8125rem;
    padding: 0.4rem 0.6rem;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.3;
  }

  :deep(.p-dropdown-item-label) {
    font-size: 0.8125rem;
  }

  .p-inputtext {
    font-size: 0.8125rem;
  }

  .p-password-input {
    font-size: 0.8125rem;
  }

  /* Ensure password field stays contained */
  :deep(.p-password) {
    max-width: 100%;
    overflow: hidden;
  }

  :deep(.p-password .p-inputtext) {
    padding-right: 2.5rem;
    font-size: 0.8125rem;
  }

  label {
    font-size: 0.8125rem;
  }
}
</style>